import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, BookOpen, Feather, Lightbulb, Quote, Sparkles, PenTool } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// Font loader for editorial aesthetic
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,500;0,8..60,600;1,8..60,400;1,8..60,500&display=swap');

    .font-editorial {
      font-family: 'Instrument Serif', 'Source Serif 4', Georgia, serif;
    }
    .font-body {
      font-family: 'Source Serif 4', Georgia, serif;
    }
    .font-sans {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }
  `}</style>
);

// Featured essays data
const featuredEssays = [
  {
    title: "The Banana Context Theory",
    date: "Jul 2025",
    premise: "A framework for understanding how AI models lose context - explained with fruit.",
    insight: "Complex ideas need simple metaphors.",
    link: "https://unengineered.substack.com/p/the-banana-context-theory"
  },
  {
    title: "System Design is Broken",
    date: "May 2025",
    premise: "We've been teaching system design wrong. Here's what actually matters.",
    insight: "Question the sacred cows.",
    link: "https://unengineered.substack.com/p/system-design-is-broken"
  },
  {
    title: "Can you explain MCP without using 'Protocol'?",
    date: "Apr 2025",
    premise: "A challenge to explain Model Context Protocol to a five-year-old.",
    insight: "Jargon is a crutch for unclear thinking.",
    link: "https://unengineered.substack.com/p/can-you-explain-mcp-without-using"
  },
  {
    title: "Does your AI agree with everything you say?",
    date: "Jul 2025",
    premise: "The sycophancy problem in AI, and why your chatbot might be lying to you.",
    insight: "Uncomfortable truths > comfortable lies.",
    link: "https://unengineered.substack.com/p/does-your-ai-agree-with-everything"
  },
  {
    title: "Etiquette of a Good Software",
    date: "Nov 2025",
    premise: "Software should have manners. Most doesn't.",
    insight: "Code is communication.",
    link: "https://unengineered.substack.com/p/etiquette-of-a-good-software"
  },
];

// Principles/pillars
const principles = [
  {
    icon: <Feather className="w-6 h-6" />,
    title: "Simplicity is Sophistication",
    description: "If you can't explain it to a five-year-old, you don't understand it yourself."
  },
  {
    icon: <Lightbulb className="w-6 h-6" />,
    title: "Question Everything",
    description: "Best practices become worst practices when followed blindly."
  },
  {
    icon: <BookOpen className="w-6 h-6" />,
    title: "Writing is Thinking",
    description: "The act of explaining clarifies your own understanding."
  },
  {
    icon: <Sparkles className="w-6 h-6" />,
    title: "Original > Regurgitated",
    description: "Create frameworks, don't just repeat documentation."
  },
];

const UnengineeredStudy = () => {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((scrolled / maxScroll) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#faf8f5] text-[#1a1a1a]">
      <FontLoader />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-[#e8e4df] z-50">
        <motion.div
          className="h-full bg-[#c41e3a]"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#faf8f5]/90 backdrop-blur-sm border-b border-[#e8e4df]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/labs')}
            className="flex items-center gap-2 text-[#666] hover:text-[#1a1a1a] transition-colors font-sans text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Labs</span>
          </motion.button>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            href="https://unengineered.substack.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[#c41e3a] hover:underline font-sans text-sm"
          >
            <span>Read on Substack</span>
            <ArrowUpRight className="w-3 h-3" />
          </motion.a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Masthead */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#999] mb-6">
              Lab Entry No. 06
            </p>

            <h1 className="font-editorial text-6xl md:text-8xl lg:text-9xl text-[#1a1a1a] mb-6 italic">
              Unengineered
            </h1>

            <div className="w-24 h-px bg-[#c41e3a] mx-auto mb-6" />

            <p className="font-body text-xl md:text-2xl text-[#444] max-w-2xl mx-auto leading-relaxed">
              Simpler words on everyday engineering, product-business, and technology
              in an otherwise complicated world.
            </p>
          </motion.div>

          {/* The Question */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1a1a1a] text-[#faf8f5] p-8 md:p-12 rounded-sm mb-20"
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-4">
              The Question
            </p>
            <p className="font-editorial text-2xl md:text-3xl italic leading-relaxed">
              "Why do engineers make simple things complicated?
              And why do we accept jargon as a substitute for understanding?"
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Manifesto */}
      <section className="py-20 px-6 bg-white border-y border-[#e8e4df]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-8 text-center">
              The Manifesto
            </p>

            <div className="font-body text-lg md:text-xl leading-relaxed space-y-6 text-[#333]">
              <p>
                I started writing because I was frustrated.
              </p>

              <p>
                Frustrated by documentation that assumed you already knew everything.
                By conference talks filled with buzzwords and empty of insight.
                By the strange gatekeeping that happens when engineers refuse to explain
                things in plain language.
              </p>

              <p className="font-editorial text-2xl md:text-3xl italic text-[#1a1a1a] py-4">
                "The curse of knowledge is real: once you understand something,
                you forget what it was like to not understand it."
              </p>

              <p>
                So I made a rule for myself: <strong>explain everything as if the reader is
                intelligent but unfamiliar</strong>. No jargon without definition. No acronyms
                without expansion. No assumptions about prior knowledge.
              </p>

              <p>
                The name "Unengineered" is intentional. It's a rejection of over-engineering -
                the tendency to make things more complicated than they need to be.
                In writing, and in code.
              </p>

              <p>
                <strong>The surprising discovery:</strong> explaining things simply doesn't
                just help readers. It helps me. Writing forces clarity. If I can't explain
                something in simple terms, it means I don't truly understand it yet.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Principles */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-12 text-center">
              The Principles
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {principles.map((principle, index) => (
                <motion.div
                  key={principle.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 border border-[#e8e4df] rounded-sm hover:border-[#c41e3a]/30 transition-colors"
                >
                  <div className="text-[#c41e3a] mb-4">{principle.icon}</div>
                  <h3 className="font-editorial text-xl mb-2">{principle.title}</h3>
                  <p className="font-body text-[#666]">{principle.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Essays */}
      <section className="py-20 px-6 bg-[#1a1a1a] text-[#faf8f5]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#666] mb-4 text-center">
              The Work
            </p>
            <h2 className="font-editorial text-3xl md:text-4xl text-center mb-16 italic">
              Featured Essays
            </h2>

            <div className="space-y-8">
              {featuredEssays.map((essay, index) => (
                <motion.a
                  key={essay.title}
                  href={essay.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="block group"
                >
                  <div className="border-b border-[#333] pb-8 hover:border-[#c41e3a] transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-sans text-xs text-[#666] mb-2">{essay.date}</p>
                        <h3 className="font-editorial text-xl md:text-2xl mb-2 group-hover:text-[#c41e3a] transition-colors">
                          {essay.title}
                        </h3>
                        <p className="font-body text-[#999] mb-3">{essay.premise}</p>
                        <p className="font-sans text-xs uppercase tracking-wider text-[#c41e3a]">
                          Insight: {essay.insight}
                        </p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#666] group-hover:text-[#c41e3a] transition-colors flex-shrink-0 mt-1" />
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Complexity Stripper */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-8 text-center">
              The Method
            </p>
            <h2 className="font-editorial text-3xl md:text-4xl text-center mb-16 italic">
              Before & After
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Before */}
              <div className="bg-[#f0eee9] p-6 rounded-sm">
                <p className="font-sans text-xs uppercase tracking-wider text-[#999] mb-4">
                  Typical Tech Explanation
                </p>
                <p className="font-mono text-sm text-[#666] leading-relaxed">
                  "MCP is a protocol that standardizes the interface between LLM applications
                  and external data sources/tools, enabling bidirectional communication through
                  a client-server architecture with support for multiple transport layers..."
                </p>
                <p className="font-sans text-xs text-[#999] mt-4">
                  Translation: ???
                </p>
              </div>

              {/* After */}
              <div className="bg-[#1a1a1a] text-[#faf8f5] p-6 rounded-sm">
                <p className="font-sans text-xs uppercase tracking-wider text-[#666] mb-4">
                  Unengineered Version
                </p>
                <p className="font-body text-[#ccc] leading-relaxed">
                  "MCP is like a universal translator. Your AI wants to talk to your calendar,
                  your database, your Slack. MCP is the language they all agree to speak.
                  One plug, everything connects."
                </p>
                <p className="font-sans text-xs text-[#c41e3a] mt-4">
                  5 seconds to understand.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pull Quote */}
      <section className="py-20 px-6 bg-[#c41e3a] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <Quote className="w-12 h-12 mx-auto mb-8 opacity-50" />
            <p className="font-editorial text-2xl md:text-4xl italic leading-relaxed mb-8">
              "The best engineers I know are not the ones who use the most complex solutions.
              They're the ones who find the simplest solution that works."
            </p>
            <p className="font-sans text-sm uppercase tracking-wider opacity-70">
              From "System Design is Broken"
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Takeaway */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-8">
              The Takeaway
            </p>

            <h2 className="font-editorial text-4xl md:text-5xl italic mb-8">
              Start Writing.
            </h2>

            <div className="font-body text-lg md:text-xl text-[#444] leading-relaxed space-y-6 max-w-2xl mx-auto text-left">
              <p>
                If you're an engineer, you should be writing. Not because it looks good on your resume.
                Because it will make you better at your job.
              </p>

              <p>
                <strong>Writing forces clarity.</strong> You can't hand-wave in prose the way you can
                in conversation. Every sentence demands precision.
              </p>

              <p>
                <strong>Teaching deepens understanding.</strong> The best way to learn something is to
                explain it to someone else. Writing is teaching at scale.
              </p>

              <p>
                <strong>Ideas compound.</strong> That blog post you write today might connect with
                someone three years from now. Your thinking becomes a searchable, shareable artifact.
              </p>
            </div>

            <div className="mt-12 pt-12 border-t border-[#e8e4df]">
              <p className="font-editorial text-xl italic text-[#666] mb-8">
                The question isn't whether you have something to say.
                <br />
                It's whether you're brave enough to simplify it.
              </p>

              <a
                href="https://unengineered.substack.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a1a1a] text-white font-sans text-sm uppercase tracking-wider hover:bg-[#333] transition-colors rounded-sm"
              >
                <BookOpen className="w-4 h-4" />
                Read Unengineered
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Topics Covered */}
      <section className="py-16 px-6 bg-[#f0eee9] border-t border-[#e8e4df]">
        <div className="max-w-4xl mx-auto">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#999] mb-8 text-center">
            Topics Explored
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'AI Systems', 'System Design', 'MCP Protocol', 'Prompt Engineering',
              'Software Philosophy', 'Agent Architecture', 'Production AI',
              'Technical Writing', 'Engineering Culture', 'Critical Thinking'
            ].map((topic) => (
              <span
                key={topic}
                className="px-4 py-2 bg-white border border-[#e8e4df] rounded-full font-sans text-sm text-[#666]"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#e8e4df]">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-sans text-sm text-[#999]">
            Published on{' '}
            <a href="https://substack.com" className="text-[#c41e3a] hover:underline">
              Substack
            </a>
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://unengineered.substack.com/archive"
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-sm text-[#666] hover:text-[#1a1a1a] transition-colors"
            >
              Archive
            </a>
            <a
              href="https://unengineered.substack.com/subscribe"
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-sm text-[#c41e3a] hover:underline"
            >
              Subscribe
            </a>
          </div>
        </div>
      </footer>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="unengineered" variant="light" />
    </div>
  );
};

export default UnengineeredStudy;
